import { CloseButton, Table, Tooltip } from '@mantine/core';
import { Text } from '@mantine/core';
import { IconArrowRight } from '@tabler/icons-react';
import { useNavigate, useParams } from 'react-router-dom';
import { NoDataFoundTableRow } from '../../../components/DataTable';
import { KeyedRow } from '../../../components/KeyedRow';
import { LoadingRows } from '../../../components/LoadingRows';
import { defaultIconStyle } from '../../../theme/styling/iconStyling';
import { query } from '../../../util/api/query';
import { buildFilterUrl } from '../../../util/convertToUrl';
import {
	formatToDateString,
	formatToTimeString,
} from '../../../util/formatting';
import type { FilterDto } from '../../../util/types/FilterDto';
import { undefinedCheck } from '../../../util/undefinedCheck';
import type { PharmacyFirstDetailDto } from '../types/PharmacyFirstDetailDto';

interface PharmacyFirstDetailsTableProps {
	branchId: number;
	operatorId: number;
	filter: FilterDto;
	startLoading: boolean;
}

export function PharmacyFirstDetailsTable({
	branchId,
	operatorId,
	filter,
	startLoading,
}: PharmacyFirstDetailsTableProps) {
	const { organisation } = useParams();
	const navigate = useNavigate();

	const detailsQuery = query<PharmacyFirstDetailDto[]>(
		`${organisation}/pharmacy-first/details?branchId=${branchId}&operatorId=${operatorId}&${buildFilterUrl(filter)}`,
		startLoading,
	);

	const hasData =
		detailsQuery.data?.value && detailsQuery.data.value.length > 0;

	return (
		<Table withRowBorders pt={0} mt={0}>
			<Table.Thead>
				<KeyedRow>
					<Table.Th>Date</Table.Th>
					<Table.Th>Time</Table.Th>
					<Table.Th>Receipt</Table.Th>
					<Table.Th>SKUID</Table.Th>
					<Table.Th>Product</Table.Th>
					<Table.Th>Status</Table.Th>
					<Table.Th>Condition</Table.Th>
					<Table.Th />
				</KeyedRow>
			</Table.Thead>
			<Table.Tbody>
				{detailsQuery.isLoading ? (
					<LoadingRows rows={1} />
				) : (
					<>
						{!hasData && <NoDataFoundTableRow />}
						{detailsQuery.data?.value.map((detail) => (
							<KeyedRow>
								<Table.Td>
									{formatToDateString(new Date(detail.dateTime))}
								</Table.Td>
								<Table.Td>
									{formatToTimeString(new Date(detail.dateTime))}
								</Table.Td>
								<Table.Td>{undefinedCheck(detail.receiptId)}</Table.Td>
								<Table.Td>{undefinedCheck(detail.skuid)}</Table.Td>
								<Tooltip label={undefinedCheck(detail.product)} disabled={detail.product.length < 30}> 
									<Table.Td>
										<Text w={200} truncate={'end'}>
											{undefinedCheck(detail.product)}
										</Text>
									</Table.Td>
								</Tooltip>
								<Table.Td>{undefinedCheck(detail.status)}</Table.Td>
								<Tooltip label={undefinedCheck(detail.condition)} disabled={detail.condition.length < 30}> 
									<Table.Td>
										<Text w={200} truncate={'end'}>
											{undefinedCheck(detail.condition)}
										</Text>
									</Table.Td>
								</Tooltip>
								<Table.Td>
									<Tooltip
										label={'Invalid receipt ID'}
										disabled={
											detail.receiptId !== null &&
											detail.receiptId !== undefined
										}
									>
										<CloseButton
										mr={20}
											disabled={
												detail.receiptId === null ||
												detail.receiptId === undefined
											}
											icon={<IconArrowRight {...defaultIconStyle} />}
											onClick={() =>
												window.open(
													`/${organisation}/receipt/${detail.receiptId}`,
												)
											}
										/>
									</Tooltip>
								</Table.Td>
							</KeyedRow>
						))}
					</>
				)}
			</Table.Tbody>
		</Table>
	);
}
